import {
  PromotionChannelListQueryVariables,
  Currency,
  PromotionStatus,
  QueryPromotionChannelsOrderByColumn,
  QueryPromotionChannelsWherePromotionColumn,
  SortOrder,
} from "@/gql/graphql";
import {
  DataTable,
  DataTableColDef,
  DataTableColumnHeader,
  GetQueryVariablesFn,
} from "@/lib/Components/DataTable/DataTable";
import {
  PromotionChannelListItem,
  promotionChannelListQuery,
} from "@/app/Promotions/GraphQL/promotionChannelListQuery";
import { Badge } from "@/components/catalyst/badge";
import { formatCurrency } from "@/lib/Formatters/formatCurrency";
import { TimeSinceNow } from "@/lib/Components/Common/TimeSinceNow";
import { Link } from "@/components/catalyst/link";
import { useTranslations } from "use-intl";
import type { TableId } from "@/app/Common/Utils/table-id";
import type { ReactNode } from "react";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { syncPromotionChannelMutation } from "@/app/Promotions/GraphQL/promotionChannelMutations";
import { EditPromotionChannelDialog } from "@/app/Promotions/Components/EditPromotionChannelDialog";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { ActionsHook } from "@/lib/Components/Actions/ActionGroupButtons";
import { PencilIcon, EyeIcon } from "@heroicons/react/24/outline";
import { RefreshCwIcon } from "lucide-react";
import { toast } from "sonner";
import { AdPreviewDialog } from "@/app/Promotions/Components/AdPreviewDialog";
import { PlatformCell } from "@/app/Promotions/Components/PlatformCell";

import { promotionStatusColorMap } from "@/app/Promotions/Utils/promotionMaps";

type PromotionChannelColumnId =
  | "relocation"
  | "platform"
  | "status"
  | "budget"
  | "impressions"
  | "clicks"
  | "conversions"
  | "spend"
  | "last_synced_at"
  | "created_at";

const sortMap: Partial<
  Record<PromotionChannelColumnId, QueryPromotionChannelsOrderByColumn>
> = {
  impressions: QueryPromotionChannelsOrderByColumn.Impressions,
  clicks: QueryPromotionChannelsOrderByColumn.Clicks,
  conversions: QueryPromotionChannelsOrderByColumn.Conversions,
  spend: QueryPromotionChannelsOrderByColumn.SpendAmount,
  created_at: QueryPromotionChannelsOrderByColumn.CreatedAt,
};

export function getPromotionChannelTableVariables({
  supplierId,
  relocationId,
  status,
}: {
  supplierId?: string;
  relocationId?: string;
  status?: PromotionStatus[];
}): GetQueryVariablesFn<
  PromotionChannelListQueryVariables,
  PromotionChannelColumnId
> {
  return ({ pagination, sorting }) => ({
    first: pagination.pageSize,
    page: pagination.pageIndex,
    supplier_id: supplierId,
    status,
    wherePromotion: relocationId
      ? {
          column: QueryPromotionChannelsWherePromotionColumn.RelocationId,
          value: relocationId,
        }
      : undefined,
    orderBy: sorting.map((sort) => ({
      column:
        sortMap[sort.id as PromotionChannelColumnId] ??
        QueryPromotionChannelsOrderByColumn.CreatedAt,
      order: sort.desc ? SortOrder.Desc : SortOrder.Asc,
    })),
  });
}

const numberFormatter = new Intl.NumberFormat("en-US");

function usePromotionChannelActions(): ActionsHook<PromotionChannelListItem> {
  const { mutateAsync: syncChannel } = useGqlMutation(
    syncPromotionChannelMutation,
  );
  const { open: openEditDialog } = useDialog(EditPromotionChannelDialog);
  const { open: openPreviewDialog } = useDialog(AdPreviewDialog);
  const t = useTranslations("promotion.actions");

  return (channel) => [
    {
      label: t("edit"),
      items: [
        {
          actions: [
            {
              label: t("preview"),
              Icon: EyeIcon,
              handleAction: () => {
                openPreviewDialog({
                  creative: channel.promotion.creative ?? null,
                });
              },
            },
            {
              label: t("edit"),
              Icon: PencilIcon,
              handleAction: () => {
                openEditDialog({
                  channelId: channel.id,
                  budgetAmount: channel.budget_amount,
                  status: channel.status,
                });
              },
            },
            {
              label: t("resync"),
              Icon: RefreshCwIcon,
              handleAction: async () => {
                await syncChannel({ id: channel.id });

                toast.success(t("resyncSuccess"));
              },
            },
          ],
        },
      ],
    },
  ];
}

export function PromotionChannelTable({
  id,
  supplierId,
  relocationId,
  status,
  rightButtons,
}: {
  id: TableId;
  supplierId?: string;
  relocationId?: string;
  status?: PromotionStatus[];
  rightButtons?: ReactNode;
}) {
  const t = useTranslations("promotion.table");
  const getActions = usePromotionChannelActions();

  const columns: DataTableColDef<
    PromotionChannelListItem,
    unknown,
    PromotionChannelColumnId
  >[] = [
    {
      id: "relocation",
      enableSorting: false,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("relocation")} />
      ),
      accessorFn: (row) => row,
      cell: ({ row }) => (
        <Link
          from="/$tenant/$orgId/supplier/{-$supplierId}"
          to="/$tenant/$orgId/supplier/{-$supplierId}/relocations/$relocationId"
          params={(prev: Record<string, unknown>) => ({
            ...prev,
            relocationId: row.original.promotion.relocation.id,
          })}
          className="whitespace-nowrap font-medium"
        >
          {row.original.promotion.relocation.name}
        </Link>
      ),
    },
    {
      id: "platform",
      enableSorting: false,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("platform")} />
      ),
      accessorFn: (row) => row.platform,
      cell: ({ row }) => (
        <PlatformCell
          platform={row.original.platform}
          managerUrl={row.original.manager_url}
        />
      ),
    },
    {
      id: "status",
      enableSorting: false,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("status")} />
      ),
      accessorFn: (row) => row.status,
      cell: ({ row }) => (
        <Badge color={promotionStatusColorMap[row.original.status]}>
          {row.original.status}
        </Badge>
      ),
    },
    {
      id: "budget",
      enableSorting: false,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("budget")} />
      ),
      accessorFn: (row) => row.budget_amount,
      cell: ({ row }) =>
        formatCurrency(row.original.budget_amount, Currency.Usd, {
          showZero: true,
        }),
    },
    {
      id: "impressions",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("impressions")} />
      ),
      accessorFn: (row) => row.impressions,
      cell: ({ row }) => numberFormatter.format(row.original.impressions),
    },
    {
      id: "clicks",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("clicks")} />
      ),
      accessorFn: (row) => row.clicks,
      cell: ({ row }) => numberFormatter.format(row.original.clicks),
    },
    {
      id: "conversions",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("conversions")} />
      ),
      accessorFn: (row) => row.conversions,
      cell: ({ row }) => numberFormatter.format(row.original.conversions),
    },
    {
      id: "spend",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("spend")} />
      ),
      accessorFn: (row) => row.spend_amount,
      cell: ({ row }) =>
        formatCurrency(row.original.spend_amount, Currency.Usd, {
          showZero: true,
        }),
    },
    {
      id: "last_synced_at",
      enableSorting: false,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("lastSynced")} />
      ),
      accessorFn: (row) => row.last_synced_at,
      cell: ({ row }) =>
        row.original.last_synced_at ? (
          <TimeSinceNow time={row.original.last_synced_at} />
        ) : (
          t("neverSynced")
        ),
    },
    {
      id: "created_at",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("created")} />
      ),
      accessorFn: (row) => row.created_at,
      cell: ({ row }) => <TimeSinceNow time={row.original.created_at} />,
    },
  ];

  return (
    <DataTable
      id={id}
      columns={columns}
      document={promotionChannelListQuery}
      accessor={(data) => data.promotionChannels}
      initialSorting={[{ id: "created_at", desc: true }]}
      getActions={getActions}
      rightButtons={rightButtons}
      hiddenColumns={relocationId ? ["relocation"] : undefined}
      getQueryVariables={getPromotionChannelTableVariables({
        supplierId,
        relocationId,
        status,
      })}
    />
  );
}
